import type { CanDoActions, CanDoRemoteConfig } from "types/CanDoActions";
import { OrganisationRole } from "types/organisation";
import type { Organisation } from "types/organisation";

const Role = OrganisationRole;

class CanDoService {
  static rolesForActions = Object.freeze({
    // Athletes
    "athlete.bulkUpload": [Role.OWNER, Role.COACH],
    "athlete.bulkEdit1rm": [Role.OWNER, Role.COACH],
    "athlete.delete": [Role.OWNER],
    "athlete.archive": [Role.OWNER, Role.COACH],
    "athlete.invite": [Role.OWNER, Role.COACH],

    // Export Data
    "export.any": [Role.OWNER, Role.COACH],

    // Organisation
    "organisation.any": [Role.OWNER],

    // Subscriptions
    "subscription.any": [Role.OWNER],

    // Support
    "support.any": [Role.OWNER, Role.COACH],

    // Charting
    "charting.any": [Role.OWNER, Role.COACH],

    // Dashboards
    "dashboard.any": [Role.OWNER, Role.COACH],

    // Leaderboards
    "leaderboards.any": [Role.OWNER, Role.COACH],
    "leaderboard.create": [Role.OWNER, Role.COACH],
    "leaderboard.view": [Role.OWNER, Role.COACH],
    "leaderboard.update": [Role.OWNER, Role.COACH],
    "leaderboard.delete": [Role.OWNER, Role.COACH],

    // Groups
    "group.any": [Role.OWNER],
    "group.create": [Role.OWNER],
    "group.assign": [Role.OWNER, Role.COACH],
    "group.view": [Role.OWNER, Role.COACH],

    // Coaches
    "coach.any": [Role.OWNER],
    "coach.view": [Role.OWNER, Role.COACH],

    /**
     * To check if the user can create custom exercises we should use useCanCreateCustomExercises hook.
     * This hook checks if the user has unlimited custom exercises or if they have reached the limit in the current subscription.
     *
     * @see useCustomExercisesUsage()
     * @see useCanCreateCustomExercises()
     */
    // Exercise Library
    "library.any": [Role.OWNER, Role.COACH],
    "library.archive": [Role.OWNER],
    "library.unlimited": [Role.OWNER, Role.COACH],

    /**
     * To check if the user can create workouts we should use useCanCreateWorkouts hook.
     * This hook checks if the user has unlimited workouts or if they have reached the limit in the current subscription.
     *
     * @see useWorkoutsUsage()
     * @see useCanCreateWorkouts()
     */
    // Workouts
    "workout.any": [Role.OWNER, Role.COACH],
    "workout.unlimited": [Role.OWNER, Role.COACH],

    // Programs
    "programs.any": [Role.OWNER, Role.COACH],

    // Calendar
    "calendar.any": [Role.OWNER, Role.COACH],

    // Hyrox
    "hyrox.any": [Role.OWNER, Role.COACH],
  });

  static check(
    action: CanDoActions,
    options: {
      role: OrganisationRole | undefined;
      organisation?: Organisation;
      remoteConfig?: CanDoRemoteConfig;
    }
  ) {
    const { role, organisation, remoteConfig } = options;
    return (
      CanDoService.checkRole(action, role) && CanDoService.checkOrganisation(action, { organisation, remoteConfig })
    );
  }

  private static checkRole(action: CanDoActions, role: OrganisationRole | undefined) {
    if (typeof role === "undefined") return false;

    const allowedRolesForAction = CanDoService.rolesForActions[action];

    if (typeof allowedRolesForAction === "undefined") {
      throw new Error(`The action you are checking permission for does not exist: ${action}`);
    }

    return allowedRolesForAction.includes(role);
  }

  private static checkOrganisation(
    action: CanDoActions,
    options: { organisation?: Organisation; remoteConfig?: CanDoRemoteConfig }
  ) {
    const { organisation } = options;

    switch (action) {
      case "charting.any":
        return Boolean(organisation?.featureConfig.isHubChartingEnabled);
      case "dashboard.any":
        return Boolean(organisation?.featureConfig.isHubDashboardsEnabled);
      case "leaderboards.any":
        return Boolean(organisation?.featureConfig.isHubLeaderboardsEnabled);
      case "export.any":
        // return Boolean(organisation?.featureConfig?.isHubExportEnabled);
        return true; // TODO: Remove this when export is implemented
      case "group.any":
      case "group.assign":
      case "group.create":
      case "group.view":
        return Boolean(organisation?.featureConfig.isGroupsEnabled);
      case "library.any":
      case "library.archive":
        /**
         * If the organisation has a subscription, we check if the subscription details are available.
         * If they are, we assume the user is part of the new subscription model and have access to custom exercises.
         */
        if (organisation && organisation.subscriptionDetails) {
          return true;
        }
        return Boolean(organisation?.featureConfig.isCustomExercisesEnabled);
      case "library.unlimited":
        /**
         * @important useCustomExercisesLimit is used to determine exercises creation limits for the user.
         */
        return Boolean(organisation?.featureConfig.isCustomExercisesEnabled);
      case "workout.any":
        /** Same logic as above for custom exercises. */
        if (organisation && organisation.subscriptionDetails) {
          return true;
        }
        return Boolean(organisation?.featureConfig.isWorkoutsEnabled);
      case "workout.unlimited":
        /**
         * @important useWorkoutsLimit is used to determine if the workouts creation limits for the user.
         */
        return Boolean(organisation?.featureConfig.isWorkoutsEnabled);
      case "programs.any":
        return (
          process.env.REACT_APP_ENABLE_PROGRAMMING === "true" &&
          Boolean(organisation?.featureConfig.isProgrammingEnabled)
        );
      case "calendar.any":
        // TODO: Enable featureConfig isCalendarEnabled when ready
        return (
          process.env.REACT_APP_ENABLE_CALENDAR === "true" && Boolean(organisation?.featureConfig.isProgrammingEnabled)
        );
      case "support.any":
        return Boolean(organisation?.featureConfig.isSupportEnabled);
      case "hyrox.any":
        return Boolean(organisation?.featureConfig.isHyroxConditioningScoreEnabled);
      default:
        return true;
    }
  }
}

export default CanDoService;
